import { signal } from '@preact/signals-core';
import { components } from '../api/v1';
import { activeRoleSignal, getAuthHeaders, searchCustomersForAutocomplete } from './assets';
import { usersListSignal, refreshUsers } from './users';
import { escapeHtml } from '../utils/sanitize';
import { formatDate, formatDateTime, formatDuration } from '../utils/formatters';
import { showToast } from '../utils/toast';

type ServiceSessionReportPage = components['schemas']['ServiceSessionReportPage'];
type ServiceSessionReportRow = components['schemas']['ServiceSessionReportRow'];
type User = components['schemas']['User'];

export const sessionReportSignal = signal<ServiceSessionReportPage | null>(null);
export const sessionReportLoadingSignal = signal<boolean>(false);
export const sessionReportErrorSignal = signal<string | null>(null);
export const sessionReportStartDateSignal = signal<string>('');
export const sessionReportEndDateSignal = signal<string>('');
export const sessionReportTechnicianIdsSignal = signal<string[]>([]);
export const sessionReportCustomerIdSignal = signal<string>('');
export const sessionReportPageSignal = signal<number>(1);

const PAGE_SIZE = 50;

function isManagerPlus(role: string | null | undefined): boolean {
  return role === 'superadmin' || role === 'admin' || role === 'manager';
}

function dateInputValue(value: Date): string {
  return value.toISOString().slice(0, 10);
}

export function initSessionReportSignals(): void {
  if (!sessionReportStartDateSignal.value) {
    const start = new Date();
    start.setDate(start.getDate() - 30);
    sessionReportStartDateSignal.value = dateInputValue(start);
  }
  if (!sessionReportEndDateSignal.value) {
    sessionReportEndDateSignal.value = dateInputValue(new Date());
  }
}

function validateDateRange(): string | null {
  const start = sessionReportStartDateSignal.value;
  const end = sessionReportEndDateSignal.value;
  if (!start || !end) return 'Choose both a start date and an end date.';
  if (start > end) return 'Start date must be on or before the end date.';
  return null;
}

export async function refreshSessionReport(): Promise<void> {
  const error = validateDateRange();
  if (error) {
    sessionReportErrorSignal.value = error;
    sessionReportSignal.value = null;
    updateSessionReportResultsRegion();
    return;
  }
  sessionReportLoadingSignal.value = true;
  sessionReportErrorSignal.value = null;
  updateSessionReportResultsRegion();
  try {
    const params = new URLSearchParams({
      startDate: sessionReportStartDateSignal.value,
      endDate: sessionReportEndDateSignal.value,
      page: String(sessionReportPageSignal.value),
      pageSize: String(PAGE_SIZE),
    });
    if (sessionReportTechnicianIdsSignal.value.length > 0) {
      params.set('technicianId', sessionReportTechnicianIdsSignal.value.join(','));
    }
    if (sessionReportCustomerIdSignal.value) {
      params.set('customerId', sessionReportCustomerIdSignal.value);
    }
    const response = await fetch(`/api/v1/reports/service-sessions?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const payload: unknown = await response.json().catch(() => null);
      const message = typeof payload === 'object' && payload !== null && 'error' in payload && typeof payload.error === 'string'
        ? payload.error
        : 'Unable to load the service session report.';
      throw new Error(message);
    }
    sessionReportSignal.value = await response.json() as ServiceSessionReportPage;
  } catch (errorValue) {
    sessionReportSignal.value = null;
    sessionReportErrorSignal.value = errorValue instanceof Error ? errorValue.message : 'Unable to load the service session report.';
  } finally {
    sessionReportLoadingSignal.value = false;
    updateSessionReportResultsRegion();
  }
}

async function downloadSessionReport(): Promise<void> {
  const error = validateDateRange();
  if (error) {
    sessionReportErrorSignal.value = error;
    return;
  }
  const button = document.getElementById('btn-download-session-report');
  button?.setAttribute('loading', 'true');
  try {
    const params = new URLSearchParams({
      startDate: sessionReportStartDateSignal.value,
      endDate: sessionReportEndDateSignal.value,
    });
    if (sessionReportTechnicianIdsSignal.value.length > 0) {
      params.set('technicianId', sessionReportTechnicianIdsSignal.value.join(','));
    }
    if (sessionReportCustomerIdSignal.value) {
      params.set('customerId', sessionReportCustomerIdSignal.value);
    }
    const response = await fetch(`/api/v1/reports/service-sessions.xlsx?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('The XLSX report could not be generated.');
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `service-sessions-${sessionReportStartDateSignal.value}-to-${sessionReportEndDateSignal.value}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
    showToast('Service session report downloaded.', 'success');
  } catch (errorValue) {
    showToast(errorValue instanceof Error ? errorValue.message : 'The XLSX report could not be generated.', 'error');
  } finally {
    button?.removeAttribute('loading');
  }
}

function renderLocation(row: ServiceSessionReportRow): string {
  if (row.siteLatitude === null || row.siteLatitude === undefined || row.siteLongitude === null || row.siteLongitude === undefined) return '—';
  return `${row.siteLatitude.toFixed(6)}, ${row.siteLongitude.toFixed(6)}`;
}

function renderRow(row: ServiceSessionReportRow): string {
  const duration = row.durationSeconds === undefined || row.durationSeconds === null
    ? '—'
    : formatDuration(row.durationSeconds);
  return `<s-table-row>
    <s-table-cell>${escapeHtml(formatDate(row.serviceDate))}</s-table-cell>
    <s-table-cell><strong>${escapeHtml(row.ticketNumber)}</strong></s-table-cell>
    <s-table-cell><span style="max-width:200px;display:inline-block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${escapeHtml(row.ticketDescription || '—')}">${escapeHtml(row.ticketDescription || '—')}</span></s-table-cell>
    <s-table-cell>${escapeHtml(row.technicianEmail)}</s-table-cell>
    <s-table-cell>${escapeHtml(row.status)}</s-table-cell>
    <s-table-cell>${escapeHtml(formatDateTime(row.startedAt))}</s-table-cell>
    <s-table-cell>${escapeHtml(row.endedAt ? formatDateTime(row.endedAt) : 'In progress')}</s-table-cell>
    <s-table-cell>${escapeHtml(duration)}</s-table-cell>
    <s-table-cell><span style="max-width:220px;display:inline-block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${escapeHtml(row.sessionNotes || '—')}">${escapeHtml(row.sessionNotes || '—')}</span></s-table-cell>
    <s-table-cell>${escapeHtml(row.customerName)}</s-table-cell>
    <s-table-cell>${escapeHtml(row.siteName || '—')}</s-table-cell>
    <s-table-cell>${escapeHtml(renderLocation(row))}</s-table-cell>
    <s-table-cell>${escapeHtml(row.assetSerialNumber || '—')}</s-table-cell>
    <s-table-cell>${escapeHtml([row.assetVendor, row.assetModel].filter(Boolean).join(' ') || '—')}</s-table-cell>
    <s-table-cell>${escapeHtml(row.parts || '—')}</s-table-cell>
    <s-table-cell>${escapeHtml(row.approvedParts || '—')}</s-table-cell>
    <s-table-cell>${escapeHtml(row.installedParts || '—')}</s-table-cell>
    <s-table-cell><span style="max-width:220px;display:inline-block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${escapeHtml(row.counters || '—')}">${escapeHtml(row.counters || '—')}</span></s-table-cell>
  </s-table-row>`;
}

function getTechnicianButtonText(allTechs: User[]): string {
  const selected = sessionReportTechnicianIdsSignal.value;
  if (selected.length === 0) return 'All technicians';
  if (selected.length === 1) {
    const tech = allTechs.find(t => t.id === selected[0]);
    return tech ? tech.email : '1 technician';
  }
  return `${selected.length} technicians`;
}

export function renderManagerSessionReportsPage(): string {
  const role = activeRoleSignal.value;
  if (!isManagerPlus(role)) {
    return `<s-page heading="Access Denied"><s-banner tone="critical">Only managers and administrators can access session reports.</s-banner></s-page>`;
  }

  initSessionReportSignals();
  const technicians = (usersListSignal.value || []).filter((user: User) => user.role === 'technician');
  const selectedTechIds = sessionReportTechnicianIdsSignal.value;

  return `
    <s-section>
      <s-box padding="base" background="surface" border="base" borderRadius="base">
        <s-stack gap="base">
          <div style="display: flex; gap: 1rem; flex-wrap: wrap; align-items: flex-end;">
            <!-- Start Date & End Date Controls -->
            <div style="display: flex; gap: 0.5rem; align-items: center;">
              <div style="display: flex; flex-direction: column; gap: 0.25rem; background: white; border: 1px solid #c4cdd5; border-radius: 6px; padding: 0.35rem 0.6rem;">
                <label style="font-size: 0.7rem; font-weight: 600; color: #637381; text-transform: uppercase;">From</label>
                <input type="date" id="session-report-start" value="${escapeHtml(sessionReportStartDateSignal.value)}" style="border: none; outline: none; font-family: inherit; font-size: 0.9rem; background: transparent; cursor: pointer;" />
              </div>
              <span style="color: #637381; font-weight: 500;">to</span>
              <div style="display: flex; flex-direction: column; gap: 0.25rem; background: white; border: 1px solid #c4cdd5; border-radius: 6px; padding: 0.35rem 0.6rem;">
                <label style="font-size: 0.7rem; font-weight: 600; color: #637381; text-transform: uppercase;">To</label>
                <input type="date" id="session-report-end" value="${escapeHtml(sessionReportEndDateSignal.value)}" style="border: none; outline: none; font-family: inherit; font-size: 0.9rem; background: transparent; cursor: pointer;" />
              </div>
            </div>

            <!-- Technician Multi-Select Component -->
            <div style="display: flex; flex-direction: column; gap: 0.25rem; position: relative;">
              <label style="font-size: 0.875rem; font-weight: 500; color: var(--p-color-text-subdued);">Technician(s)</label>
              <button type="button" id="tech-dropdown-toggle" style="background: white; border: 1px solid #c4cdd5; border-radius: 6px; padding: 0.5rem 0.75rem; min-height: 42px; font-size: 0.9rem; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; min-width: 200px; text-align: left; box-sizing: border-box;">
                <span id="tech-dropdown-label" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHtml(getTechnicianButtonText(technicians))}</span>
                <span style="font-size: 0.75rem; color: #637381;">▼</span>
              </button>
              
              <!-- Multi-select dropdown popover -->
              <div id="tech-dropdown-menu" style="display: none; position: fixed; width: 280px; background: white; border: 1px solid #c4cdd5; border-radius: 6px; box-shadow: 0 4px 16px rgba(0,0,0,0.2); z-index: 10000; padding: 0.5rem; box-sizing: border-box;">
                <div style="display: flex; justify-content: space-between; padding-bottom: 0.5rem; margin-bottom: 0.5rem; border-bottom: 1px solid #e1e3e5; font-size: 0.8rem;">
                  <button type="button" id="btn-tech-select-all" style="background: none; border: none; color: #008060; cursor: pointer; font-weight: 600; padding: 0;">Select all</button>
                  <button type="button" id="btn-tech-clear-all" style="background: none; border: none; color: #d33; cursor: pointer; font-weight: 600; padding: 0;">Clear all</button>
                </div>
                <div style="max-height: 200px; overflow-y: auto; display: flex; flex-direction: column; gap: 0.4rem;">
                  ${technicians.length === 0 ? `<span style="font-size:0.85rem; color:#637381; padding:0.25rem;">No technicians found</span>` : technicians.map(t => {
    const isChecked = selectedTechIds.includes(t.id);
    return `<label style="display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; cursor: pointer; padding: 0.2rem 0.25rem; border-radius: 4px;">
                      <input type="checkbox" class="tech-checkbox" value="${escapeHtml(t.id)}" ${isChecked ? 'checked' : ''} style="cursor: pointer;" />
                      <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHtml(t.email)}</span>
                    </label>`;
  }).join('')}
                </div>
              </div>
            </div>

            <!-- Customer Organization Floating Autocomplete -->
            <div style="display: flex; flex-direction: column; gap: 0.25rem; min-width: 240px; position: relative;">
              <opm-autocomplete
                id="session-report-customer"
                floating-dropdown
                label="Customer Organization"
                placeholder="All customers (search...)"
                selected-id="${escapeHtml(sessionReportCustomerIdSignal.value)}"
              ></opm-autocomplete>
            </div>

            <div style="display: flex; gap: 0.5rem; align-items: flex-end; flex-wrap: wrap;">
              <s-button id="btn-run-session-report" variant="primary" style="min-height: 42px;">Generate report</s-button>
              <s-button id="btn-download-session-report" style="min-height: 42px;">Download XLSX</s-button>
              <s-button id="btn-download-exec-pdf-report" style="min-height: 42px; background-color: #CC0000; border-color: #CC0000; color: white;">✦ Executive PDF Report</s-button>
            </div>
          </div>

          <!-- Manager Overrides Panel for Client-Facing Reports -->
          <div style="background: #FAFAFA; border: 1px solid #E2E8F0; border-left: 4px solid #CC0000; border-radius: 4px; padding: 0.75rem 1rem; margin-top: 0.5rem;">
            <div style="display: flex; justify-content: space-between; align-items: center; cursor: pointer;" id="exec-overrides-toggle">
              <span style="font-size: 0.85rem; font-weight: 600; color: #1E293B; display: flex; align-items: center; gap: 0.4rem;">
                <span style="color: #CC0000;">✦</span> Executive PDF Overrides (Technician Timing Adjustments)
              </span>
              <span style="font-size: 0.75rem; color: #64748B;" id="exec-overrides-icon">▼</span>
            </div>
            <div id="exec-overrides-content" style="display: none; margin-top: 0.75rem; gap: 1rem; flex-wrap: wrap;">
              <div style="display: flex; flex-direction: column; gap: 0.25rem; flex: 1; min-width: 260px;">
                <label style="font-size: 0.75rem; font-weight: 600; color: #475569;">Custom Report Title</label>
                <input type="text" id="exec-report-title-input" placeholder="Monthly Service & Asset Performance Report" style="padding: 0.4rem 0.6rem; border: 1px solid #CBD5E1; border-radius: 4px; font-size: 0.85rem;" />
              </div>
              <div style="display: flex; flex-direction: column; gap: 0.25rem; width: 200px;">
                <label style="font-size: 0.75rem; font-weight: 600; color: #475569;">Custom Total Service Hours</label>
                <input type="number" step="0.5" id="exec-report-hours-input" placeholder="Auto-calculated" style="padding: 0.4rem 0.6rem; border: 1px solid #CBD5E1; border-radius: 4px; font-size: 0.85rem;" />
              </div>
              <div style="font-size: 0.75rem; color: #64748B; align-self: center; max-width: 320px;">
                <em>Note: Technician timing errors and untimely logs are omitted on the PDF timeline. Custom hours override the summary card count.</em>
              </div>
            </div>
          </div>

          </div>
        </s-stack>
      </s-box>
    </s-section>
    <div id="session-report-results-region">
      ${renderSessionReportResultsHtml()}
    </div>
  `;
}

function renderSessionReportResultsHtml(): string {
  const error = sessionReportErrorSignal.peek();
  const loading = sessionReportLoadingSignal.peek();
  const report = sessionReportSignal.peek();
  const rows = report?.items || [];
  const currentPage = report?.page || 1;
  const totalPages = Math.max(1, Math.ceil((report?.total || 0) / (report?.pageSize || PAGE_SIZE)));
  const tableRows = rows.map(session => renderRow(session)).join('');

  return `
    ${error ? `<s-banner tone="critical" title="Report unavailable">${escapeHtml(error)}</s-banner>` : ''}
    ${loading ? '<s-box padding="base"><s-spinner accessibilityLabel="Loading report"></s-spinner></s-box>' : ''}
    <s-section>
      <s-box padding="none" background="surface" border="base" borderRadius="base" style="overflow-x:auto;">
        ${!loading && rows.length === 0 ? '<div style="padding:3rem;text-align:center;"><s-text tone="subdued">No service sessions found for this period.</s-text></div>' : `<s-table>
          <s-table-header-row>
            <s-table-header>Date</s-table-header><s-table-header>Ticket #</s-table-header><s-table-header>Ticket Description</s-table-header><s-table-header>Technician</s-table-header><s-table-header>Status</s-table-header><s-table-header>Started</s-table-header><s-table-header>Ended</s-table-header><s-table-header>Duration</s-table-header><s-table-header>Session Notes</s-table-header><s-table-header>Customer</s-table-header><s-table-header>Site</s-table-header><s-table-header>Location</s-table-header><s-table-header>Asset serial</s-table-header><s-table-header>Machine / model</s-table-header><s-table-header>Requested Parts (Colloquial)</s-table-header><s-table-header>Approved Parts (Mapped)</s-table-header><s-table-header>Installed Parts (Mapped)</s-table-header><s-table-header>Meter Counters</s-table-header>
          </s-table-header-row><s-table-body>${tableRows}</s-table-body>
        </s-table>`}
      </s-box>
      ${report ? `<s-stack direction="inline" gap="base" style="justify-content:space-between;align-items:center;padding:1rem 0;"><s-text tone="subdued">${report.total} session(s) · Page ${currentPage} of ${totalPages}</s-text><s-stack direction="inline" gap="small"><s-button id="btn-session-report-prev" ${currentPage <= 1 ? 'disabled' : ''}>Previous</s-button><s-button id="btn-session-report-next" ${currentPage >= totalPages ? 'disabled' : ''}>Next</s-button></s-stack></s-stack>` : ''}
    </s-section>
  `;
}

function updateSessionReportResultsRegion(): void {
  const region = document.getElementById('session-report-results-region');
  if (region) {
    region.innerHTML = renderSessionReportResultsHtml();
    bindSessionReportPagination();
  }
}

function bindSessionReportPagination(): void {
  document.getElementById('btn-session-report-prev')?.addEventListener('click', () => {
    sessionReportPageSignal.value = Math.max(1, sessionReportPageSignal.value - 1);
    void refreshSessionReport();
  });
  document.getElementById('btn-session-report-next')?.addEventListener('click', () => {
    sessionReportPageSignal.value += 1;
    void refreshSessionReport();
  });
}

export function setupManagerSessionReportsPageListeners(): void {
  if (!isManagerPlus(activeRoleSignal.value)) return;
  if ((usersListSignal.value || []).length === 0) {
    void refreshUsers();
  }

  bindSessionReportDomEvents();
  bindSessionReportPagination();
}

function bindSessionReportDomEvents(): void {

  const startInput = document.getElementById('session-report-start') as HTMLInputElement | null;
  const endInput = document.getElementById('session-report-end') as HTMLInputElement | null;

  startInput?.addEventListener('change', () => { sessionReportStartDateSignal.value = startInput.value; });
  endInput?.addEventListener('change', () => { sessionReportEndDateSignal.value = endInput.value; });

  // Executive Overrides Toggle
  const toggleBtn = document.getElementById('exec-overrides-toggle');
  const toggleContent = document.getElementById('exec-overrides-content');
  const toggleIcon = document.getElementById('exec-overrides-icon');
  if (toggleBtn && toggleContent && toggleIcon) {
    toggleBtn.addEventListener('click', () => {
      const isHidden = toggleContent.style.display === 'none';
      toggleContent.style.display = isHidden ? 'flex' : 'none';
      toggleIcon.textContent = isHidden ? '▲' : '▼';
    });
  }

  // Technician Multi-Select Toggle & Listeners
  const techToggle = document.getElementById('tech-dropdown-toggle');
  const techMenu = document.getElementById('tech-dropdown-menu');
  const techLabel = document.getElementById('tech-dropdown-label');
  const btnSelectAll = document.getElementById('btn-tech-select-all');
  const btnClearAll = document.getElementById('btn-tech-clear-all');

  if (techToggle && techMenu) {
    const positionTechMenu = () => {
      if (techToggle && techMenu && techMenu.style.display === 'block') {
        const rect = techToggle.getBoundingClientRect();
        techMenu.style.position = 'fixed';
        techMenu.style.top = `${rect.bottom + 4}px`;
        techMenu.style.left = `${rect.left}px`;
        techMenu.style.width = `${Math.max(280, rect.width)}px`;
        techMenu.style.zIndex = '10000';
      }
    };

    techMenu.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    techToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      const isVisible = techMenu.style.display === 'block';
      if (isVisible) {
        techMenu.style.display = 'none';
      } else {
        techMenu.style.display = 'block';
        positionTechMenu();
      }
    });

    window.addEventListener('scroll', positionTechMenu, true);
    window.addEventListener('resize', positionTechMenu);

    document.addEventListener('click', (e) => {
      if (!techToggle.contains(e.target as Node) && !techMenu.contains(e.target as Node)) {
        techMenu.style.display = 'none';
      }
    });

    const updateTechSelection = () => {
      const checkboxes = techMenu.querySelectorAll<HTMLInputElement>('.tech-checkbox');
      const selected: string[] = [];
      checkboxes.forEach(cb => {
        if (cb.checked) selected.push(cb.value);
      });
      sessionReportTechnicianIdsSignal.value = selected;

      const technicians = (usersListSignal.value || []).filter((user: User) => user.role === 'technician');
      if (techLabel) {
        techLabel.textContent = getTechnicianButtonText(technicians);
      }
    };

    techMenu.querySelectorAll<HTMLInputElement>('.tech-checkbox').forEach(cb => {
      cb.addEventListener('change', updateTechSelection);
    });

    btnSelectAll?.addEventListener('click', () => {
      techMenu.querySelectorAll<HTMLInputElement>('.tech-checkbox').forEach(cb => { cb.checked = true; });
      updateTechSelection();
    });

    btnClearAll?.addEventListener('click', () => {
      techMenu.querySelectorAll<HTMLInputElement>('.tech-checkbox').forEach(cb => { cb.checked = false; });
      updateTechSelection();
    });
  }

  // Floating Customer Autocomplete
  const custAutocomplete = document.getElementById('session-report-customer') as any;
  if (custAutocomplete) {
    custAutocomplete.loadItems = searchCustomersForAutocomplete;
    custAutocomplete.addEventListener('select', (e: CustomEvent) => {
      sessionReportCustomerIdSignal.value = e.detail?.id || '';
    });
  }

  document.getElementById('btn-run-session-report')?.addEventListener('click', () => {
    sessionReportPageSignal.value = 1;
    void refreshSessionReport();
  });
  document.getElementById('btn-download-session-report')?.addEventListener('click', () => void downloadSessionReport());
  document.getElementById('btn-download-exec-pdf-report')?.addEventListener('click', () => void downloadExecutiveCustomerReportPDF());
  document.getElementById('btn-session-report-prev')?.addEventListener('click', () => {
    sessionReportPageSignal.value = Math.max(1, sessionReportPageSignal.value - 1);
    void refreshSessionReport();
  });
  document.getElementById('btn-session-report-next')?.addEventListener('click', () => {
    sessionReportPageSignal.value += 1;
    void refreshSessionReport();
  });
}

async function downloadExecutiveCustomerReportPDF(): Promise<void> {
  const error = validateDateRange();
  if (error) {
    sessionReportErrorSignal.value = error;
    return;
  }
  const custId = sessionReportCustomerIdSignal.value;
  if (!custId) {
    showToast('Please select a Customer Organization to generate an Executive PDF Report.', 'warning');
    return;
  }

  const titleInput = document.getElementById('exec-report-title-input') as HTMLInputElement | null;
  const hoursInput = document.getElementById('exec-report-hours-input') as HTMLInputElement | null;

  const customTitle = titleInput?.value?.trim() || undefined;
  const customHoursRaw = hoursInput?.value?.trim();
  const customHours = customHoursRaw ? parseFloat(customHoursRaw) : undefined;

  const button = document.getElementById('btn-download-exec-pdf-report');
  button?.setAttribute('loading', 'true');
  try {
    const payload: any = {
      customerId: custId,
      startDate: sessionReportStartDateSignal.value,
      endDate: sessionReportEndDateSignal.value,
    };
    if (customTitle) payload.reportTitle = customTitle;
    if (customHours !== undefined && !isNaN(customHours)) payload.customServiceHours = customHours;

    const authHeaders = getAuthHeaders();
    const response = await fetch('/api/v1/reports/customer-summary.pdf', {
      method: 'POST',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      let jsonMsg = '';
      try {
        const parsed = JSON.parse(errText);
        jsonMsg = parsed.error || parsed.message || '';
      } catch {}
      throw new Error(jsonMsg || errText || 'The Executive PDF report could not be generated.');
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `executive-report-${sessionReportStartDateSignal.value}-to-${sessionReportEndDateSignal.value}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
    showToast('Executive PDF Report downloaded successfully.', 'success');
  } catch (errorValue) {
    showToast(errorValue instanceof Error ? errorValue.message : 'The PDF report could not be generated.', 'error');
  } finally {
    button?.removeAttribute('loading');
  }
}

export function unmountManagerSessionReportsPage(): void {}
